import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { flushSync } from "react-dom";

const AuthContext = createContext(null);

/**
 * AuthProvider:
 * - Uses cookie-based auth (credentials: include)
 * - Reads current session via GET /api/me
 * - Exposes refreshUser(), login(), logout() for app-wide use
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const res = await fetch("/api/me", { credentials: "include" });
      const data = res.ok ? await res.json() : null;
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async ({ email, password, remember }) => {
    let res;
    try {
      res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, remember }),
      });
    } catch (e) {
      const raw = e?.message || String(e);
      if (raw === "Failed to fetch" || raw.includes("NetworkError")) {
        throw new Error(
          "Cannot reach the server. Start the backend on port 5000 " +
            "(e.g. python backend/app.py from the repo root). If you use npm start on port 3000, " +
            "the API is proxied to http://127.0.0.1:5000.",
        );
      }
      throw e;
    }

    if (!res.ok) {
      const contentType = res.headers.get("content-type") || "";
      let msg = "";
      if (contentType.includes("application/json")) {
        const data = await res.json().catch(() => null);
        if (res.status === 403 && data?.error === "email_not_verified") {
          msg = data?.detail || data?.error || "";
        } else {
          msg = data?.error || data?.message || "";
        }
      } else {
        msg = (await res.text().catch(() => "")) || "";
      }

      // Never show raw HTML error pages to users.
      if (msg.includes("<!doctype") || msg.includes("<html")) msg = "";

      throw new Error(msg || "Invalid email or password");
    }

    let meRes;
    try {
      meRes = await fetch("/api/me", { credentials: "include" });
    } catch (e) {
      const raw = e?.message || String(e);
      if (raw === "Failed to fetch" || raw.includes("NetworkError")) {
        throw new Error(
          "Login succeeded but the server became unreachable before your session could load. " +
            "Ensure the backend is running on port 5000.",
        );
      }
      throw e;
    }
    if (!meRes.ok)
      throw new Error("Login succeeded but session could not be loaded");

    const me = await meRes.json();
    setUser(me);
    return me;
  };

  const logout = async () => {
    try {
      await fetch("/api/logout", { method: "POST", credentials: "include" });
    } catch {
      // ignore
    } finally {
      // Commit before callers navigate to /login; otherwise Login's effect can still
      // see the previous user and immediately redirect back to /dashboard.
      flushSync(() => {
        setUser(null);
      });
    }
  };

  useEffect(() => {
    refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({ user, loading, refreshUser, login, logout }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
